import { ON_USER_NUMBERS_EVENT } from "./hub/registry.ts";

/** Semáforo por línea (peor estado gana si hay varias llamadas). */
export type LineTrafficLight = "green" | "blue" | "yellow" | "red";

const LIGHT_RANK: Record<LineTrafficLight, number> = {
  red: 4,
  yellow: 3,
  blue: 2,
  green: 1,
};

export interface LineSnapshotEntry {
  id: number;
  name: string;
  digits: string;
  userIds: number[];
}

/** Agente donde suena un timbre (`CallRinging_On_Agent`); puede haber varios a la vez en la misma llamada. */
export interface RingingAgentRef {
  id: number;
  name?: string;
}

export interface CallRuntime {
  numberId: number;
  /** Contestada / en conversación o hold */
  isLive: boolean;
  /** Entrante en cola: aún sin agente en payload */
  pendingInboundNoUser: boolean;
  /** Agente asignado cuando se conoce */
  assignedUserId?: number;
  /**
   * Agentes en los que está sonando la llamada (mismo `call.id`, varios `Ringing_On_Agent`).
   * Se limpia al contestar. La UI muestra todos los nombres, no solo el último evento.
   */
  ringingUsers?: RingingAgentRef[];
  direction?: string;
  /** `raw_digits` del payload Aircall (entrante: quien llama; saliente: destino marcado). */
  rawDigits?: string;
  /** `user.name` del agente cuando el payload lo trae */
  agentName?: string;
  /** Inicio de llamada según payload (`started_at`), si existe (ms). */
  startedAtMs?: number;
  /** Contestación atendida: prioridad sobre `startedAtMs` para reloj/duración UI. */
  answeredAtMs?: number;
  /** Instante del último evento hub que actualizó esta sesión (cliente). */
  lastEventAt: number;
}

export interface LineMonitorState {
  linesById: Map<number, LineSnapshotEntry>;
  callsById: Map<number, CallRuntime>;
  /** Nombres de agentes conocidos por id (desde snapshot `UserNumbers.users`). */
  userNamesById: Map<number, string>;
  /** Usuario explícitamente desconectado (si llegan eventos `UserDisconnected`) */
  disconnectedUserIds: Set<number>;
}

/** Fase de cada sesión en la UI (no confundir con el peor semáforo de la línea). */
export type ActiveCallPhase = "live" | "ringing" | "queued";

/** Detalle de fila: hora y contador comparten ancla según fase (creación → contestación). */
export interface ActiveCallDetail {
  /**
   * Instante mostrado en reloj (`<time>`) y origen del contador:
   * - sin contestar: creación de sesión (`startedAtMs`);
   * - ya contestada: instante de contestación (`answeredAtMs`).
   */
  atMs: number;
  /** `true` cuando hay `answeredAtMs` en runtime (contador = tiempo de llamada tras contestar). */
  isAnswered: boolean;
  /** Fase interna para ordenar filas (contestación / timbre / cola). */
  phase: ActiveCallPhase;
  /** Semáforo de esta sesión (fila): azul contestación, amarillo timbre/espera, rojo crítico. */
  rowLight: LineTrafficLight;
  direction: "inbound" | "outbound" | "unknown";
  digits: string;
  /** Nombre del agente / usuario en snapshot; omitido si no se conoce. */
  userLabel?: string;
}

export interface LineViewRow {
  id: number;
  name: string;
  digits: string;
  activeCallCount: number;
  light: LineTrafficLight;
  /** Detalle por cada llamada activa (hora, dirección, número, usuario). */
  activeCalls: ActiveCallDetail[];
}

export function createInitialLineMonitorState(): LineMonitorState {
  return {
    linesById: new Map(),
    callsById: new Map(),
    userNamesById: new Map(),
    disconnectedUserIds: new Set(),
  };
}

/** Quita sufijos `_Group` / `_Number` para unificar SIMPLE y ámbito línea. */
export function stripHubEventSuffixes(name: string): string {
  let n = name;
  if (n.endsWith("_Group")) n = n.slice(0, -"_Group".length);
  if (n.endsWith("_Number")) n = n.slice(0, -"_Number".length);
  return n;
}

interface CallPayloadShape {
  id?: number;
  /** Unix segundos o ms según origen Aircall; normalizamos a ms UTC. */
  started_at?: number;
  answered_at?: number;
  raw_digits?: string;
  direction?: string;
  status?: string;
  number?: { id?: number; name?: string; digits?: string };
  user?: { id?: number; name?: string; email?: string };
}

function asRecord(x: unknown): Record<string, unknown> | null {
  return x !== null && typeof x === "object" ? (x as Record<string, unknown>) : null;
}

function asCallPayload(item: unknown): CallPayloadShape | null {
  const o = asRecord(item);
  if (!o) return null;
  const id = o["id"];
  const numberRaw = o["number"];
  const userRaw = o["user"];
  const num =
    numberRaw && typeof numberRaw === "object"
      ? (numberRaw as Record<string, unknown>)
      : null;
  const usr =
    userRaw && typeof userRaw === "object"
      ? (userRaw as Record<string, unknown>)
      : null;
  return {
    id: typeof id === "number" ? id : undefined,
    started_at:
      typeof o["started_at"] === "number" && Number.isFinite(o["started_at"])
        ? o["started_at"]
        : undefined,
    answered_at:
      typeof o["answered_at"] === "number" && Number.isFinite(o["answered_at"])
        ? o["answered_at"]
        : undefined,
    raw_digits:
      typeof o["raw_digits"] === "string" ? o["raw_digits"] : undefined,
    direction: typeof o["direction"] === "string" ? o["direction"] : undefined,
    status: typeof o["status"] === "string" ? o["status"] : undefined,
    number: num
      ? {
          id: typeof num["id"] === "number" ? num["id"] : undefined,
          name: typeof num["name"] === "string" ? num["name"] : undefined,
          digits: typeof num["digits"] === "string" ? num["digits"] : undefined,
        }
      : undefined,
    user:
      usr && typeof usr["id"] === "number"
        ? {
            id: usr["id"],
            name: typeof usr["name"] === "string" ? usr["name"] : undefined,
            email: typeof usr["email"] === "string" ? usr["email"] : undefined,
          }
        : undefined,
  };
}

function parseUserNumbersPayload(
  item: unknown,
  into: Map<number, LineSnapshotEntry>,
  userNamesById: Map<number, string>,
): void {
  if (!Array.isArray(item)) return;
  for (const row of item) {
    const o = asRecord(row);
    if (!o) continue;
    const id = o["id"];
    if (typeof id !== "number") continue;
    const name = typeof o["name"] === "string" ? o["name"] : `Línea ${id}`;
    const digits = typeof o["digits"] === "string" ? o["digits"] : "";
    const usersRaw = o["users"];
    const userIds: number[] = [];
    if (Array.isArray(usersRaw)) {
      for (const u of usersRaw) {
        const ur = asRecord(u);
        if (ur && typeof ur["id"] === "number") {
          userIds.push(ur["id"]);
          const uname = ur["name"];
          if (typeof uname === "string" && uname.length > 0) {
            userNamesById.set(ur["id"], uname);
          }
        }
      }
    }
    into.set(id, { id, name, digits, userIds });
  }
}

/** Normaliza `answered_at` Aircall a ms (UTC). */
function answeredAtMsFromPayload(p: CallPayloadShape): number | undefined {
  const t = p.answered_at;
  if (typeof t !== "number" || !Number.isFinite(t)) return undefined;
  return t > 1e12 ? t : t * 1000;
}

/** Normaliza `started_at` Aircall a ms (UTC). */
function startedAtMsFromPayload(p: CallPayloadShape): number | undefined {
  const t = p.started_at;
  if (typeof t !== "number" || !Number.isFinite(t)) return undefined;
  return t > 1e12 ? t : t * 1000;
}

function mergeStartedAt(
  incoming: number | undefined,
  prev: number | undefined,
): number | undefined {
  if (incoming === undefined) return prev;
  if (prev === undefined) return incoming;
  return Math.min(prev, incoming);
}

function patchFromCallPayload(p: CallPayloadShape): Pick<
  CallRuntime,
  "rawDigits" | "agentName"
> & {
  startedAtMs?: number;
  answeredAtMs?: number;
} {
  const rawDigits = p.raw_digits?.trim() || undefined;
  const agentName = p.user?.name?.trim() || undefined;
  const startedAtMs = startedAtMsFromPayload(p);
  const answeredAtMs = answeredAtMsFromPayload(p);
  return {
    rawDigits,
    agentName,
    ...(startedAtMs !== undefined ? { startedAtMs } : {}),
    ...(answeredAtMs !== undefined ? { answeredAtMs } : {}),
  };
}

/** Igual que `patchFromCallPayload` pero sin tocar `agentName` (para no pisar agente ya fijado). */
function patchFromCallPayloadSansAgent(p: CallPayloadShape): Pick<
  CallRuntime,
  "rawDigits"
> & {
  startedAtMs?: number;
  answeredAtMs?: number;
} {
  const rawDigits = p.raw_digits?.trim() || undefined;
  const startedAtMs = startedAtMsFromPayload(p);
  const answeredAtMs = answeredAtMsFromPayload(p);
  return {
    rawDigits,
    ...(startedAtMs !== undefined ? { startedAtMs } : {}),
    ...(answeredAtMs !== undefined ? { answeredAtMs } : {}),
  };
}

function isInboundDirection(dir: string | undefined): boolean {
  const d = dir?.toLowerCase() ?? "";
  return d === "inbound" || d === "in";
}

/** Nombre para UI: varios timbres → todos los agentes; contestada → un agente. */
function resolveUserDisplayName(
  state: LineMonitorState,
  c: CallRuntime,
): string | undefined {
  if (c.answeredAtMs !== undefined) {
    if (c.agentName?.trim()) return c.agentName.trim();
    if (c.assignedUserId !== undefined) {
      const fromSnap = state.userNamesById.get(c.assignedUserId);
      if (fromSnap) return fromSnap;
    }
    return undefined;
  }
  const ringers = c.ringingUsers;
  if (ringers !== undefined && ringers.length > 0) {
    const labels = ringers.map((r) => {
      const n = r.name?.trim();
      if (n) return n;
      const fromSnap = state.userNamesById.get(r.id);
      if (fromSnap) return fromSnap;
      return `Usuario ${r.id}`;
    });
    return [...new Set(labels)].join(", ");
  }
  if (c.agentName?.trim()) return c.agentName.trim();
  if (c.assignedUserId !== undefined) {
    const fromSnap = state.userNamesById.get(c.assignedUserId);
    if (fromSnap) return fromSnap;
  }
  return undefined;
}

function deriveActiveCallPhase(c: CallRuntime): ActiveCallPhase {
  if (c.isLive || c.answeredAtMs !== undefined) return "live";
  if ((c.ringingUsers?.length ?? 0) > 0) return "ringing";
  if (c.pendingInboundNoUser) return "queued";
  if (isInboundDirection(c.direction)) return "queued";
  return "ringing";
}

function callDirectionKind(c: CallRuntime): ActiveCallDetail["direction"] {
  const raw = c.direction?.toLowerCase() ?? "";
  if (raw === "outbound" || raw === "out") return "outbound";
  if (raw === "inbound" || raw === "in") return "inbound";
  return "unknown";
}

/** Una entrada por sesión de llamada en esa línea. Orden: primero en curso, luego sonando, luego cola; dentro de cada grupo, más reciente primero. */
export function buildActiveCallDetailsForLine(
  state: LineMonitorState,
  lineId: number,
): ActiveCallDetail[] {
  const rows: ActiveCallDetail[] = [];
  for (const c of state.callsById.values()) {
    if (c.numberId !== lineId) continue;
    const rd = c.rawDigits?.trim();
    const digits = rd && rd.length > 0 ? rd : "—";
    const userLabel = resolveUserDisplayName(state, c);
    const isAnswered = c.answeredAtMs !== undefined;
    const phase = deriveActiveCallPhase(c);
    const rowLight = trafficLightForCall(state, lineId, c);
    const atMs = isAnswered
      ? c.answeredAtMs!
      : (c.startedAtMs ?? c.lastEventAt);
    rows.push({
      atMs,
      isAnswered,
      phase,
      rowLight,
      direction: callDirectionKind(c),
      digits,
      userLabel,
    });
  }
  const phaseOrder: Record<ActiveCallPhase, number> = {
    live: 0,
    ringing: 1,
    queued: 2,
  };
  rows.sort((a, b) => {
    const da = phaseOrder[a.phase];
    const db = phaseOrder[b.phase];
    if (da !== db) return da - db;
    return b.atMs - a.atMs;
  });
  return rows;
}

function userIsBusy(state: LineMonitorState, userId: number): boolean {
  for (const c of state.callsById.values()) {
    if (!c.isLive) continue;
    if (c.assignedUserId === userId) return true;
  }
  return false;
}

function freeAgentsOnLine(state: LineMonitorState, line: LineSnapshotEntry): number {
  let n = 0;
  for (const uid of line.userIds) {
    if (state.disconnectedUserIds.has(uid)) continue;
    if (!userIsBusy(state, uid)) n += 1;
  }
  return n;
}

function worseLight(
  a: LineTrafficLight,
  b: LineTrafficLight,
): LineTrafficLight {
  return LIGHT_RANK[a] >= LIGHT_RANK[b] ? a : b;
}

/** Semáforo de una sola sesión (misma lógica que cada iteración de `lineTrafficLight`). */
function trafficLightForCall(
  state: LineMonitorState,
  lineId: number,
  c: CallRuntime,
): LineTrafficLight {
  const line = state.linesById.get(lineId);
  if (c.isLive) return "blue";
  const inboundPreAnswer =
    isInboundDirection(c.direction) && c.answeredAtMs === undefined;
  if (inboundPreAnswer) {
    if (c.pendingInboundNoUser) {
      const free = line ? freeAgentsOnLine(state, line) : 0;
      return free === 0 ? "red" : "yellow";
    }
    return "yellow";
  }
  if (c.pendingInboundNoUser) {
    const free = line ? freeAgentsOnLine(state, line) : 0;
    return free === 0 ? "red" : "yellow";
  }
  return "blue";
}

function lineTrafficLight(
  state: LineMonitorState,
  lineId: number,
): LineTrafficLight {
  const calls = [...state.callsById.values()].filter(
    (c) => c.numberId === lineId,
  );
  if (calls.length === 0) return "green";

  let acc: LineTrafficLight = "green";
  for (const c of calls) {
    acc = worseLight(acc, trafficLightForCall(state, lineId, c));
  }
  return acc;
}

function countActiveLineCalls(state: LineMonitorState, lineId: number): number {
  let n = 0;
  for (const c of state.callsById.values()) {
    if (c.numberId === lineId) n += 1;
  }
  return n;
}

/** Filas ordenadas: rojo → amarillo → azul → verde; dentro del mismo color, más llamadas activas primero; desempate por nombre. */
export function selectLineViewRows(state: LineMonitorState): LineViewRow[] {
  const rows: LineViewRow[] = [];
  for (const line of state.linesById.values()) {
    const light = lineTrafficLight(state, line.id);
    const activeCalls = buildActiveCallDetailsForLine(state, line.id);
    rows.push({
      id: line.id,
      name: line.name,
      digits: line.digits,
      activeCallCount: countActiveLineCalls(state, line.id),
      light,
      activeCalls,
    });
  }
  const order: LineTrafficLight[] = ["red", "yellow", "blue", "green"];
  rows.sort((a, b) => {
    const ia = order.indexOf(a.light);
    const ib = order.indexOf(b.light);
    if (ia !== ib) return ia - ib;
    if (b.activeCallCount !== a.activeCallCount) {
      return b.activeCallCount - a.activeCallCount;
    }
    return a.name.localeCompare(b.name, "es");
  });
  return rows;
}

/**
 * Líneas con actividad (no solo verde) o, si todas están inactivas, una sola (la primera del orden ya aplicado).
 */
export function rowsWithActivityOrOneFallback(rows: LineViewRow[]): LineViewRow[] {
  const busy = rows.filter((r) => r.light !== "green");
  if (busy.length > 0) return busy;
  if (rows.length > 0) return [rows[0]];
  return [];
}

function upsertCall(
  state: LineMonitorState,
  callId: number,
  patch: Partial<CallRuntime> & { numberId: number },
): LineMonitorState {
  const prev = state.callsById.get(callId);
  let startedAtMs = mergeStartedAt(patch.startedAtMs, prev?.startedAtMs);
  if (startedAtMs === undefined && prev === undefined) {
    startedAtMs = Date.now();
  }
  const next: CallRuntime = {
    numberId: patch.numberId,
    isLive: patch.isLive ?? prev?.isLive ?? false,
    pendingInboundNoUser:
      patch.pendingInboundNoUser ?? prev?.pendingInboundNoUser ?? false,
    assignedUserId: patch.assignedUserId ?? prev?.assignedUserId,
    ringingUsers:
      patch.ringingUsers !== undefined
        ? patch.ringingUsers
        : prev?.ringingUsers,
    direction: patch.direction ?? prev?.direction,
    rawDigits: patch.rawDigits ?? prev?.rawDigits,
    agentName: patch.agentName ?? prev?.agentName,
    startedAtMs,
    answeredAtMs:
      patch.answeredAtMs !== undefined
        ? patch.answeredAtMs
        : prev?.answeredAtMs,
    lastEventAt: Date.now(),
  };
  const m = new Map(state.callsById);
  m.set(callId, next);
  return { ...state, callsById: m };
}

function removeCall(state: LineMonitorState, callId: number): LineMonitorState {
  const m = new Map(state.callsById);
  m.delete(callId);
  return { ...state, callsById: m };
}

function applyCallCreated(
  state: LineMonitorState,
  p: CallPayloadShape,
): LineMonitorState {
  const callId = p.id;
  const numId = p.number?.id;
  if (callId === undefined || numId === undefined) return state;
  const dir = p.direction;
  const hasUser = p.user !== undefined && p.user.id !== undefined;
  if (dir === "outbound") {
    return upsertCall(state, callId, {
      numberId: numId,
      isLive: hasUser,
      pendingInboundNoUser: false,
      assignedUserId: hasUser ? p.user!.id : undefined,
      direction: dir,
      ...patchFromCallPayload(p),
    });
  }
  const inbound =
    dir === "inbound" || dir === "in" ? true : dir === "outbound" ? false : true;
  return upsertCall(state, callId, {
    numberId: numId,
    isLive: false,
    pendingInboundNoUser: inbound && !hasUser,
    assignedUserId: hasUser ? p.user!.id : undefined,
    direction: dir,
    ...patchFromCallPayload(p),
  });
}

function applyCallAnsweredOrSimilar(
  state: LineMonitorState,
  p: CallPayloadShape,
  live: boolean,
  /** Solo en CallAnswered: si no viene `answered_at`, fijar instante de contestación en cliente. */
  useAnsweredClockFallback = false,
): LineMonitorState {
  const callId = p.id;
  const numId = p.number?.id;
  if (callId === undefined || numId === undefined) return state;
  const hasUser = p.user !== undefined && p.user.id !== undefined;
  const prev = state.callsById.get(callId);
  const patch = patchFromCallPayload(p);
  let answeredAtMs = patch.answeredAtMs;
  if (
    useAnsweredClockFallback &&
    answeredAtMs === undefined &&
    live &&
    hasUser &&
    prev?.answeredAtMs === undefined
  ) {
    answeredAtMs = Date.now();
  }
  return upsertCall(state, callId, {
    numberId: numId,
    isLive: live,
    pendingInboundNoUser: live ? false : !hasUser,
    assignedUserId: hasUser ? p.user!.id : undefined,
    direction: p.direction,
    ...patch,
    ...(answeredAtMs !== undefined ? { answeredAtMs } : {}),
    ringingUsers: [],
  });
}

/**
 * Reduce el estado ante un evento hub. Idempotente respecto a duplicados SIMPLE / `_Number`
 * para la misma `call.id`.
 */
export function reduceLineMonitorState(
  state: LineMonitorState,
  hubEvent: string,
  _user: unknown,
  item: unknown,
): LineMonitorState {
  if (hubEvent === ON_USER_NUMBERS_EVENT) {
    const linesById = new Map(state.linesById);
    const userNamesById = new Map(state.userNamesById);
    parseUserNumbersPayload(item, linesById, userNamesById);
    return { ...state, linesById, userNamesById };
  }

  const canon = stripHubEventSuffixes(hubEvent);
  const p = asCallPayload(item);

  if (
    canon === "UserConnected" ||
    canon === "UserOpened" ||
    canon === "UserCreated"
  ) {
    const ur = asRecord(item);
    const id = ur && typeof ur["id"] === "number" ? ur["id"] : undefined;
    if (id === undefined) return state;
    const disc = new Set(state.disconnectedUserIds);
    disc.delete(id);
    const displayName =
      ur && typeof ur["name"] === "string" ? ur["name"].trim() : "";
    if (displayName.length > 0) {
      const userNamesById = new Map(state.userNamesById);
      userNamesById.set(id, displayName);
      return { ...state, disconnectedUserIds: disc, userNamesById };
    }
    return { ...state, disconnectedUserIds: disc };
  }
  if (canon === "UserDisconnected" || canon === "UserDeleted") {
    const ur = asRecord(item);
    const id = ur && typeof ur["id"] === "number" ? ur["id"] : undefined;
    if (id === undefined) return state;
    const disc = new Set(state.disconnectedUserIds);
    disc.add(id);
    return { ...state, disconnectedUserIds: disc };
  }

  if (!p || p.id === undefined) return state;

  switch (canon) {
    case "CallCreated":
      return applyCallCreated(state, p);
    case "CallRinging_On_Agent": {
      const numId = p.number?.id;
      if (p.id === undefined || numId === undefined) return state;
      const prev = state.callsById.get(p.id);
      const dir = p.direction ?? prev?.direction ?? "inbound";
      const uid = p.user?.id;
      const uname =
        p.user && typeof p.user.name === "string"
          ? p.user.name.trim()
          : undefined;
      const hasUser = uid !== undefined;
      const inboundAnsweredLocked =
        prev !== undefined &&
        prev.answeredAtMs !== undefined &&
        isInboundDirection(prev.direction);
      if (inboundAnsweredLocked) {
        return upsertCall(state, p.id, {
          numberId: numId,
          isLive: prev.isLive,
          pendingInboundNoUser: prev.pendingInboundNoUser,
          direction: dir,
          ...patchFromCallPayloadSansAgent(p),
        });
      }
      const base = prev?.ringingUsers ?? [];
      let ringingUsers = base;
      if (hasUser) {
        const idx = base.findIndex((r) => r.id === uid);
        if (idx === -1) {
          ringingUsers = [
            ...base,
            { id: uid, ...(uname !== undefined && uname.length > 0 ? { name: uname } : {}) },
          ];
        } else if (uname !== undefined && uname.length > 0 && !base[idx]?.name) {
          ringingUsers = base.map((r, i) =>
            i === idx ? { ...r, name: uname } : r,
          );
        }
      }
      return upsertCall(state, p.id, {
        numberId: numId,
        isLive: false,
        pendingInboundNoUser: ringingUsers.length === 0,
        assignedUserId:
          uid ??
          (ringingUsers.length > 0
            ? ringingUsers[ringingUsers.length - 1]!.id
            : undefined),
        direction: dir,
        ringingUsers,
        ...patchFromCallPayloadSansAgent(p),
      });
    }
    case "CallAnswered":
      return applyCallAnsweredOrSimilar(state, p, true, true);
    case "CallUnhold":
      return applyCallAnsweredOrSimilar(state, p, true, false);
    case "CallHold":
      return applyCallAnsweredOrSimilar(state, p, true, false);
    case "CallHungup":
    case "CallEnded":
      return removeCall(state, p.id);
    case "CallAgent_Declined": {
      const numId = p.number?.id;
      if (p.id === undefined || numId === undefined) return state;
      const prev = state.callsById.get(p.id);
      const uid = p.user?.id;
      if (
        prev !== undefined &&
        uid !== undefined &&
        prev.ringingUsers !== undefined &&
        prev.ringingUsers.length > 0
      ) {
        const nextRing = prev.ringingUsers.filter((r) => r.id !== uid);
        const stillPreAnswer =
          prev.answeredAtMs === undefined && !prev.isLive;
        return upsertCall(state, p.id, {
          numberId: numId,
          ringingUsers: nextRing,
          pendingInboundNoUser:
            stillPreAnswer && nextRing.length === 0
              ? true
              : stillPreAnswer
                ? false
                : prev.pendingInboundNoUser,
          assignedUserId:
            nextRing.length > 0
              ? nextRing[nextRing.length - 1]!.id
              : undefined,
          ...patchFromCallPayloadSansAgent(p),
        });
      }
      return upsertCall(state, p.id, {
        numberId: numId,
        assignedUserId: uid,
        ...patchFromCallPayload(p),
      });
    }
    case "CallTransferred":
    case "CallUnsuccessful_Transfer":
    case "CallVoicemail_Left": {
      const numId = p.number?.id;
      if (p.id === undefined || numId === undefined) return state;
      const xferUser = p.user && p.user.id !== undefined;
      return upsertCall(state, p.id, {
        numberId: numId,
        assignedUserId: xferUser ? p.user!.id : undefined,
        ...patchFromCallPayload(p),
      });
    }
    default:
      return state;
  }
}
